import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Download, CreditCard, Eye, Mail } from 'lucide-react';
import PdfViewerModal from './PdfViewerModal';
import EmailSendModal from './EmailSendModal';
import InvoiceForm from './InvoiceForm';
import InvoiceViewModal from './InvoiceViewModal';
import InvoicePaymentModal from './InvoicePaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { impayee: 'bg-red-50 text-red-600', partielle: 'bg-amber-50 text-amber-600', payee: 'bg-green-50 text-green-600', annulee: 'bg-gray-100 text-gray-500' };
const STATUS_LIST = ['impayee', 'partielle', 'payee', 'annulee'];

const STATUS_LABELS = { impayee: 'Impayée', partielle: 'Partielle', payee: 'Payée', annulee: 'Annulée' };
const INVOICE_STATUS_LABEL = (s) => STATUS_LABELS[s] || s;

const emptyItem = { type: 'service', description: '', quantity: 1, unit_price: 0, tva_rate: 20 };
const emptyForm = { client_id: '', vehicle_id: '', items: [{ ...emptyItem }], discount: 0, notes: '', due_date: '', status: 'impayee', deposit: 0, payment_method: '' };

export default function InvoicesModule({ authHeaders, refreshStats }) {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const [payForm, setPayForm] = useState({ amount: 0, method: 'especes', notes: '' });
  const [showQuickClient, setShowQuickClient] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [pdfView, setPdfView] = useState(null);
  const [emailModal, setEmailModal] = useState(null);

  const fetchData = useCallback(async () => {
    const [iRes, cRes, vRes, sRes] = await Promise.all([
      axios.get(`${API_URL}/api/crm/invoices`, { headers: authHeaders, params: filter ? { status: filter } : {} }),
      axios.get(`${API_URL}/api/crm/clients`, { headers: authHeaders }),
      axios.get(`${API_URL}/api/crm/client-vehicles`, { headers: authHeaders }),
      axios.get(`${API_URL}/api/crm/settings`, { headers: authHeaders })
    ]);
    setInvoices(iRes.data); setClients(cRes.data); setVehicles(vRes.data); setCompany(sRes.data);
  }, [filter, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/crm/invoices/${editing.id}`, form, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/crm/invoices`, form, { headers: authHeaders });
      setShowForm(false); setEditing(null); fetchData(); refreshStats?.();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette facture ?')) {
      await axios.delete(`${API_URL}/api/crm/invoices/${id}`, { headers: authHeaders });
      fetchData(); refreshStats?.();
    }
  };

  const handlePayment = async () => {
    try {
      await axios.post(`${API_URL}/api/crm/payments`, { invoice_id: showPayment, ...payForm }, { headers: authHeaders });
      setShowPayment(null); fetchData(); refreshStats?.();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const openPDF = (id, number) => {
    const token = sessionStorage.getItem('mca_auth_token');
    if (!token) { alert('Session expirée, veuillez vous reconnecter'); return; }
    setPdfView({ url: `${API_URL}/api/crm/invoices/${id}/pdf?token=${token}`, number: number || id });
  };

  const openEdit = async (id) => {
    const res = await axios.get(`${API_URL}/api/crm/invoices/${id}`, { headers: authHeaders });
    const inv = res.data;
    setForm({ client_id: inv.client_id, vehicle_id: inv.vehicle_id || '', items: inv.items?.length ? inv.items : [{ ...emptyItem }], discount: inv.discount || 0, notes: inv.notes || '', due_date: inv.due_date?.slice(0, 10) || '', status: inv.status, deposit: inv.deposit || 0, payment_method: inv.payment_method || '' });
    setEditing(inv); setShowForm(true);
  };

  const openView = async (id) => {
    const res = await axios.get(`${API_URL}/api/crm/invoices/${id}`, { headers: authHeaders });
    setViewing(res.data);
  };

  const openNew = () => {
    setForm({ ...emptyForm, items: [{ ...emptyItem }] });
    setEditing(null);
    setShowForm(true);
  };

  return (
    <div data-testid="invoices-module">
      {/* Filters + Add button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!filter ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600'}`}>Toutes</button>
          {STATUS_LIST.map(s => <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${filter === s ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600'}`}>{INVOICE_STATUS_LABEL(s)}</button>)}
        </div>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700" data-testid="add-invoice-btn"><Plus size={16} /> Nouvelle facture</button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="text-left p-3">N°</th><th className="text-left p-3">Client</th><th className="text-left p-3 hidden md:table-cell">Date</th><th className="text-right p-3">Total</th><th className="text-right p-3 hidden md:table-cell">Payé</th><th className="text-center p-3">Statut</th><th className="text-right p-3">Actions</th></tr></thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-mono font-medium">
                    <div className="flex items-center gap-1.5">
                      {inv.number}
                      {inv.email_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full" title={`Envoyé ${inv.email_count} fois`} data-testid={`email-badge-invoice-${inv.id}`}>
                          <Mail size={9} /> {inv.email_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">{inv.client_name}</td>
                  <td className="p-3 hidden md:table-cell text-gray-500">{inv.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-right font-semibold">{inv.total?.toLocaleString('fr-FR')}€</td>
                  <td className="p-3 text-right hidden md:table-cell text-gray-600">{inv.paid_amount?.toLocaleString('fr-FR')}€</td>
                  <td className="p-3 text-center"><span className={`text-xs px-2 py-1 rounded-full capitalize ${STATUS_COLORS[inv.status] || ''}`}>{inv.status}</span></td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openView(inv.id)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Consulter" data-testid={`view-invoice-${inv.id}`}><Eye size={15} /></button>
                      <button onClick={() => openEdit(inv.id)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Modifier"><Pencil size={15} /></button>
                      <button onClick={() => openPDF(inv.id, inv.number)} className="p-1.5 hover:bg-purple-50 rounded text-purple-600" title="Télécharger PDF" data-testid={`pdf-invoice-${inv.id}`}><Download size={15} /></button>
                      <button onClick={() => setEmailModal({ id: inv.id, number: inv.number, clientName: inv.client_name, clientEmail: inv.client_email })} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Envoyer par email" data-testid={`email-invoice-${inv.id}`}><Mail size={15} /></button>
                      {inv.status !== 'payee' && <button onClick={() => { setShowPayment(inv.id); setPayForm({ amount: inv.total - (inv.paid_amount || 0), method: 'especes', notes: '' }); }} className="p-1.5 hover:bg-green-50 rounded text-green-600" title="Paiement"><CreditCard size={15} /></button>}
                      <button onClick={() => handleDelete(inv.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Supprimer"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!invoices.length && <tr><td colSpan={7} className="p-8 text-center text-gray-400">Aucune facture</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showPayment && (
        <InvoicePaymentModal
          payForm={payForm}
          setPayForm={setPayForm}
          onSubmit={handlePayment}
          onClose={() => setShowPayment(null)}
        />
      )}

      {showForm && (
        <InvoiceForm
          form={form} setForm={setForm}
          editing={editing} clients={clients} vehicles={vehicles}
          emptyItem={emptyItem}
          showQuickClient={showQuickClient} setShowQuickClient={setShowQuickClient}
          setClients={setClients}
          authHeaders={authHeaders}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}

      {viewing && (
        <InvoiceViewModal
          viewing={viewing}
          company={company}
          onClose={() => setViewing(null)}
          onEdit={() => { setViewing(null); openEdit(viewing.id); }}
          onPDF={() => openPDF(viewing.id, viewing.number)}
          onEmail={() => setEmailModal({ id: viewing.id, number: viewing.number, clientName: viewing.client_name, clientEmail: viewing.client_email })}
        />
      )}

      {pdfView && (
        <PdfViewerModal url={pdfView.url} title={pdfView.number} onClose={() => setPdfView(null)} />
      )}

      {emailModal && (
        <EmailSendModal
          docType="invoice"
          docId={emailModal.id}
          docNumber={emailModal.number}
          clientName={emailModal.clientName}
          clientEmail={emailModal.clientEmail}
          authHeaders={authHeaders}
          onClose={() => setEmailModal(null)}
          onSent={() => { fetchData(); if (viewing) openView(viewing.id); }}
        />
      )}
    </div>
  );
}
